// src/connectors/components/ConnectionCard.jsx
//
// One registered connection, rendered as a card (per the requirements doc's
// recommendation to use cards rather than a plain table, since provider
// icon + name + status + last-tested reads better together — mirrors
// golive/components/Cards/ServiceCard.jsx's card shape).

import PropTypes from "prop-types";
import { RefreshCw, Power } from "lucide-react";
import { StatusBadge, Button } from "../../shared/ui";
import { getProvider } from "../constants";
import ProviderIcon from "./ProviderIcon";

function formatTimestamp(iso) {
  if (!iso) return "Never tested";
  const d = new Date(iso);
  return `Tested ${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function ConnectionCard({ family, connection, onTest, testing }) {
  const provider = getProvider(family, connection.provider);

  return (
    <div className="bg-white border border-line rounded-xl p-4 flex flex-col gap-3 shadow-[var(--jade-sh-1)]">
      <div className="flex items-start gap-3">
        <ProviderIcon initials={provider?.initials ?? "?"} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink truncate">{connection.name}</p>
          <p className="text-xs text-muted mt-0.5">{provider?.label ?? connection.provider}</p>
        </div>
        <StatusBadge map="connection" status={testing ? "testing" : connection.status} size="sm" />
      </div>

      {connection.status === "failed" && connection.lastError && (
        <p className="text-xs text-status-critical bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
          {connection.lastError}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-line">
        <span className="text-[11px] text-muted">{formatTimestamp(connection.lastTestedAt)}</span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onTest(connection)}
            disabled={testing}
            loading={testing}
          >
            <RefreshCw size={13} /> Test
          </Button>
          <Button variant="ghost" size="sm" disabled title="Not available yet">
            <Power size={13} /> Deactivate
          </Button>
        </div>
      </div>
    </div>
  );
}

ConnectionCard.propTypes = {
  family: PropTypes.string.isRequired,
  connection: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    status: PropTypes.string,
    lastTestedAt: PropTypes.string,
    lastError: PropTypes.string,
  }).isRequired,
  onTest: PropTypes.func.isRequired,
  testing: PropTypes.bool,
};
